import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FolderOpen,
    Pin,
    Download,
    BookOpenCheck,
    ChevronDown,
    Lightbulb,
    Zap,
    Loader2,
    FileText,
    Presentation,
    FileSpreadsheet,
    Image as ImageIcon,
    File as FileIcon,
} from "lucide-react";

/**
 * Iter-31 — Ressources Ambassadeurs (rendu côté ambassadeur).
 * Chaque ressource = fichier original téléchargeable + "fiche de lecture"
 * structurée (générée en coulisses) restituée dans un design premium.
 */
const FILE_TYPE_META = {
    pdf: { label: "PDF", color: "#ef4444", Icon: FileText },
    ppt: { label: "Présentation", color: "#fb923c", Icon: Presentation },
    pptx: { label: "Présentation", color: "#fb923c", Icon: Presentation },
    doc: { label: "Document", color: "#60a5fa", Icon: FileText },
    docx: { label: "Document", color: "#60a5fa", Icon: FileText },
    txt: { label: "Texte", color: "#9ca3af", Icon: FileText },
    md: { label: "Texte", color: "#9ca3af", Icon: FileText },
    csv: { label: "Tableur", color: "#34d399", Icon: FileSpreadsheet },
    xls: { label: "Tableur", color: "#34d399", Icon: FileSpreadsheet },
    xlsx: { label: "Tableur", color: "#34d399", Icon: FileSpreadsheet },
    png: { label: "Image", color: "#a78bfa", Icon: ImageIcon },
    jpg: { label: "Image", color: "#a78bfa", Icon: ImageIcon },
    jpeg: { label: "Image", color: "#a78bfa", Icon: ImageIcon },
    webp: { label: "Image", color: "#a78bfa", Icon: ImageIcon },
};

export function fileMeta(ext) {
    return FILE_TYPE_META[(ext || "").toLowerCase()] || { label: "Fichier", color: "#9ca3af", Icon: FileIcon };
}

export function formatBytes(n) {
    if (!n || n <= 0) return null;
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
    return `${(n / (1024 * 1024)).toFixed(1).replace(".0", "")} Mo`;
}

export function formatResourceDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

/** Rendu premium de la fiche de lecture structurée. */
export function MemoView({ memo, accent = "#EE722E" }) {
    if (!memo) return null;
    return (
        <div className="space-y-5" data-testid="resource-memo-view">
            {memo.intro && (
                <p className="text-sm text-white/75 font-light leading-relaxed border-l-2 pl-3"
                   style={{ borderColor: `${accent}66` }}>
                    {memo.intro}
                </p>
            )}
            {(memo.sections || []).map((s, i) => (
                <div key={i} className="space-y-2">
                    <div className="flex items-center gap-2.5">
                        <span
                            className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-extrabold shrink-0"
                            style={{ background: `${accent}1F`, color: accent }}
                        >
                            {i + 1}
                        </span>
                        <h4 className="text-sm font-extrabold text-white leading-tight">
                            {s.heading}
                        </h4>
                    </div>
                    <ul className="space-y-1.5 pl-1">
                        {(s.points || []).map((p, pi) => (
                            <li key={pi} className="flex items-start gap-2.5 text-[13px] text-white/80 leading-relaxed">
                                <span
                                    className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                                    style={{ background: accent }}
                                />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            {(memo.key_takeaways || []).length > 0 && (
                <div
                    className="rounded-2xl p-4 border"
                    style={{ background: `${accent}0D`, borderColor: `${accent}40` }}
                    data-testid="memo-key-takeaways"
                >
                    <p className="inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-[0.22em] uppercase mb-2.5"
                       style={{ color: accent }}>
                        <Zap className="w-3 h-3" />
                        À retenir absolument
                    </p>
                    <ul className="space-y-1.5">
                        {memo.key_takeaways.map((p, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[13px] font-semibold text-white/90 leading-relaxed">
                                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: accent }} />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {(memo.terrain_tips || []).length > 0 && (
                <div className="rounded-2xl p-4 border border-shariti-yellow/30 bg-shariti-yellow/8" data-testid="memo-terrain-tips">
                    <p className="inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-[0.22em] uppercase text-shariti-yellow mb-2.5">
                        <Lightbulb className="w-3 h-3" />
                        Conseils terrain
                    </p>
                    <ul className="space-y-1.5">
                        {memo.terrain_tips.map((p, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/85 leading-relaxed">
                                <span className="w-1.5 h-1.5 rounded-full bg-shariti-yellow mt-1.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function ResourceCard({ r, idx }) {
    const [open, setOpen] = useState(false);
    const meta = fileMeta(r.file_ext);
    const Icon = meta.Icon;
    const size = formatBytes(r.file_size);
    const hasMemo = r.memo_status === "ready" && r.memo;
    const isImage = ["png", "jpg", "jpeg", "webp"].includes((r.file_ext || "").toLowerCase());

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: idx * 0.04 }}
            className={`relative rounded-2xl border overflow-hidden transition-all ${
                r.pinned
                    ? "border-shariti-orange/35 bg-gradient-to-br from-shariti-orange/10 to-shariti-orange/2"
                    : "border-white/10 bg-white/4"
            }`}
            data-testid={`resource-card-${idx}`}
        >
            {r.pinned && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-shariti-orange-light z-10">
                    <Pin className="w-2.5 h-2.5" />
                    Épinglé
                </span>
            )}
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
                        style={{ background: `${meta.color}1A`, borderColor: `${meta.color}45` }}
                    >
                        <Icon className="w-5 h-5" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-base font-extrabold text-white leading-tight mb-1 pr-12">
                            {r.title}
                        </h4>
                        {r.description && (
                            <p className="text-[13px] text-white/65 font-light leading-relaxed mb-1.5">
                                {r.description}
                            </p>
                        )}
                        <p className="text-[10px] text-white/40 font-light">
                            <span
                                className="font-extrabold tracking-wider uppercase"
                                style={{ color: meta.color }}
                            >
                                {meta.label}
                            </span>
                            {size && <> · {size}</>}
                            {r.created_at && <> · {formatResourceDate(r.created_at)}</>}
                        </p>
                    </div>
                </div>

                {isImage && (
                    <img
                        src={r.file_url}
                        alt=""
                        loading="lazy"
                        className="mt-3 w-full max-h-64 object-cover rounded-xl border border-white/10"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                )}

                <div className="flex flex-wrap items-center gap-2 mt-3.5">
                    {hasMemo && (
                        <button
                            type="button"
                            onClick={() => setOpen((o) => !o)}
                            data-testid={`resource-read-${idx}`}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold transition-all ${
                                open
                                    ? "bg-shariti-orange text-white"
                                    : "bg-shariti-orange/15 border border-shariti-orange/40 text-shariti-orange-light hover:bg-shariti-orange/25"
                            }`}
                        >
                            <BookOpenCheck className="w-3.5 h-3.5" />
                            {open ? "Fermer la fiche" : "Lire la fiche"}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                    )}
                    {r.memo_status === "generating" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-white/55 bg-white/5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Fiche de lecture en préparation…
                        </span>
                    )}
                    <a
                        href={r.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={r.file_name || true}
                        data-testid={`resource-download-${idx}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold text-white/75 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white transition-all"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Télécharger l'original
                    </a>
                </div>
            </div>

            <AnimatePresence initial={false}>
                {open && hasMemo && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-5 pt-1 border-t border-white/8 mx-4 mb-1">
                            {r.memo.headline && (
                                <p className="text-lg font-extrabold text-white tracking-tight mt-4 mb-3">
                                    {r.memo.headline}
                                </p>
                            )}
                            <MemoView memo={r.memo} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function ResourcesSection({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-center py-10" data-testid="resources-empty">
                <div className="w-16 h-16 rounded-3xl bg-shariti-orange/10 flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-7 h-7 text-shariti-orange/70" />
                </div>
                <p className="text-white/55 italic text-sm max-w-md mx-auto leading-relaxed">
                    Pas encore de ressources partagées par l'association.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <p className="text-sm text-white/55 font-light leading-relaxed mb-2">
                Les <span className="text-shariti-orange-light font-semibold">documents partagés par l'association</span> pour ta campagne — chaque ressource est résumée en fiche de lecture express, et l'original reste téléchargeable.
            </p>
            <div className="space-y-3">
                {items.map((r, idx) => (
                    <ResourceCard key={r.resource_id} r={r} idx={idx} />
                ))}
            </div>
        </div>
    );
}
